<template>
  <div class="signed-sheet">
    <div class="signed-sheet-heading">
      <div class="signed-sheet-icon">
        <fa-icon :icon="['fal', icon]" />
      </div>
      <div class="signed-sheet-heading-text">
        <div class="signed-sheet-title">{{ title }}</div>
        <div class="signed-sheet-subtitle" v-if="subtitle">{{ subtitle }}</div>
      </div>
      <div class="signed-sheet-date" v-if="date">
        <fa-icon :icon="['fal', 'calendar-alt']" />
        <span>{{ date | date }}</span>
      </div>
    </div>
    <slot />
  </div>
</template>

<script>
export default {
  props: {
    icon: {
      type: String,
      default: 'file-alt',
    },
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: '',
    },
    date: {
      type: String,
      default: '',
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

$signed-sheet-inset: $layout-spacing-lg + .2rem - $border-width;

.signed-card-signature {
  padding: $layout-spacing $layout-spacing 0;
  &.is-signed {
    padding: $layout-spacing-lg $layout-spacing-lg 0;
    .digital-signature-banner {
      flex-wrap: wrap;
      margin-bottom: .6rem;
    }
    .banner-content {
      flex: 1 1 12rem;
    }
    .banner-actions {
      flex-wrap: wrap;
      justify-content: flex-end;
      margin-left: auto;
    }
  }
}

.signed-card-content {
  padding: 0 $layout-spacing-lg $layout-spacing-lg;
}

.signed-sheet {
  background-color: $light-color;
  border: $border-width solid $border-color;
  border-radius: $border-radius;
  overflow: hidden;
  .signed-sheet-heading {
    align-items: center;
    border-bottom: $border-width solid $border-color;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing;
    padding: .6rem $layout-spacing-lg .6rem $signed-sheet-inset;
  }
  .signed-sheet-icon {
    align-items: center;
    background-color: lighten($primary-color, 34%);
    border-radius: 50%;
    color: $primary-color;
    display: flex;
    flex: 0 0 1.8rem;
    font-size: $font-size-lg;
    height: 1.8rem;
    justify-content: center;
  }
  .signed-sheet-heading-text {
    flex: 1 1 10rem;
    min-width: 0;
  }
  .signed-sheet-title {
    color: $gray-color-900;
    font-size: $font-size-lg;
    font-weight: 700;
    line-height: 1.25;
  }
  .signed-sheet-subtitle {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    margin-top: .1rem;
  }
  .signed-sheet-date {
    align-items: center;
    border: $border-width solid $border-color;
    border-radius: 1rem;
    color: $gray-color-dark;
    display: inline-flex;
    font-size: $font-size-sm;
    font-weight: 600;
    gap: .3rem;
    line-height: 1;
    margin-left: auto;
    padding: .3rem .6rem;
    white-space: nowrap;
  }
  .signed-sheet-fields {
    display: grid;
    gap: .6rem $layout-spacing-lg;
    grid-template-columns: repeat(auto-fill, minmax(9rem, 1fr));
    padding: .8rem $layout-spacing-lg .8rem $signed-sheet-inset;
  }
  .signed-sheet-field-label {
    color: $gray-color-dark;
    font-size: $font-size-xs;
  }
  .signed-sheet-field-value {
    color: $gray-color-900;
    font-size: $font-size-sm;
    font-weight: 600;
    line-height: 1.4;
    margin-top: .05rem;
    overflow-wrap: anywhere;
  }
  .signed-sheet-section {
    border-top: $border-width solid $border-color;
    padding: .8rem $layout-spacing-lg .8rem $signed-sheet-inset;
  }
  .signed-sheet-section-title {
    align-items: center;
    color: $gray-color-900;
    display: flex;
    font-weight: 600;
    gap: .4rem;
    margin-bottom: .5rem;
  }
  .signed-sheet-count {
    border: $border-width solid $border-color;
    border-radius: 1rem;
    color: $gray-color-dark;
    font-size: $font-size-xs;
    font-weight: 600;
    line-height: 1;
    padding: .15rem .4rem;
  }
  .signed-sheet-rows {
    border: $border-width solid $border-color;
    border-radius: $border-radius;
  }
  .signed-sheet-row {
    align-items: flex-start;
    display: flex;
    gap: .6rem;
    padding: .55rem .7rem;
    & + .signed-sheet-row {
      border-top: $border-width solid $border-color;
    }
  }
  .signed-sheet-index {
    align-items: center;
    background-color: $gray-color-ultra-light;
    border: $border-width solid $border-color;
    border-radius: 50%;
    color: $gray-color-dark;
    display: flex;
    flex: 0 0 1.2rem;
    font-size: $font-size-xs;
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    height: 1.2rem;
    justify-content: center;
  }
  .signed-sheet-row-content {
    flex: 1;
    min-width: 0;
  }
  .signed-sheet-row-name {
    color: $gray-color-900;
    font-weight: 700;
    line-height: 1.4;
    overflow-wrap: anywhere;
  }
  .signed-sheet-row-meta {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    font-variant-numeric: tabular-nums;
    margin-top: .05rem;
  }
  .signed-sheet-row-quantity {
    color: $gray-color-900;
    flex-shrink: 0;
    font-size: $font-size-sm;
    font-weight: 600;
    white-space: nowrap;
  }
  .signed-sheet-pills {
    display: flex;
    flex-wrap: wrap;
    gap: .3rem;
  }
  .signed-sheet-pill {
    align-items: center;
    background-color: $light-color;
    border: $border-width solid $border-color;
    border-radius: 1rem;
    color: $gray-color-900;
    display: inline-flex;
    font-size: $font-size-xs;
    font-weight: 600;
    gap: .25rem;
    line-height: 1.2;
    max-width: 100%;
    padding: .22rem .5rem;
    &.is-primary {
      border-color: lighten($primary-color, 20%);
      color: $primary-color;
    }
  }
  .signed-sheet-pill-key {
    color: $gray-color-dark;
    font-weight: 400;
  }
  .signed-sheet-text {
    background-color: $gray-color-ultra-light;
    border-radius: $border-radius;
    color: $gray-color-900;
    font-size: $font-size-sm;
    line-height: 1.6;
    overflow-wrap: anywhere;
    padding: .5rem .7rem;
    white-space: pre-line;
  }
  .signed-sheet-empty {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    padding: $layout-spacing-lg;
    text-align: center;
  }
  .signed-sheet-html {
    color: $gray-color-900;
    font-size: $font-size;
    line-height: 1.7;
    margin: 0 auto;
    max-width: 36rem;
    overflow-wrap: anywhere;
    padding: 1.4rem 1.6rem 1.6rem;
    > :first-child {
      margin-top: 0;
    }
    > :last-child {
      margin-bottom: 0;
    }
    p {
      margin: 0 0 .6rem;
    }
    h1, h2, h3, h4, h5, h6 {
      color: $gray-color-900;
      font-size: $font-size-lg;
      font-weight: 700;
      line-height: 1.3;
      margin: 1rem 0 .5rem;
    }
    ul, ol {
      margin: 0 0 .6rem 1.2rem;
    }
    table {
      border-collapse: collapse;
      margin: 0 0 .8rem;
      width: 100%;
      td, th {
        border: $border-width solid $border-color;
        font-size: $font-size-sm;
        padding: .3rem .5rem;
        vertical-align: top;
      }
    }
    img {
      height: auto;
      max-width: 100%;
    }
  }
}

@media (max-width: $size-sm) {
  .signed-card-signature.is-signed {
    padding: $layout-spacing $layout-spacing 0;
  }
  .signed-card-content {
    padding: 0 $layout-spacing $layout-spacing;
  }
  .signed-sheet {
    .signed-sheet-heading-text {
      flex-basis: calc(100% - 2.2rem);
    }
    .signed-sheet-date {
      margin-left: 2.2rem;
    }
    .signed-sheet-html {
      padding: 1rem;
    }
  }
}
</style>
